import type {
  SchoolStudentRosterImportResponse,
  SchoolStudentRosterImportRowInput,
} from "@workspace/api-client-react";
import * as XLSX from "xlsx";

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_DATA_ROWS = 2000;

function normalizedHeader(value: unknown): string {
  return String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function columnIndex(headers: string[], aliases: string[]): number {
  return headers.findIndex(header => aliases.includes(header));
}

function decodeCsv(bytes: ArrayBuffer): string {
  const data = new Uint8Array(bytes);
  let encoding = "utf-8";
  let offset = 0;
  if (data[0] === 0xff && data[1] === 0xfe) {
    encoding = "utf-16le";
    offset = 2;
  } else if (data[0] === 0xfe && data[1] === 0xff) {
    encoding = "utf-16be";
    offset = 2;
  } else if (data[0] === 0xef && data[1] === 0xbb && data[2] === 0xbf) {
    offset = 3;
  } else {
    const sample = data.subarray(0, Math.min(data.length, 200));
    let evenNulls = 0;
    let oddNulls = 0;
    for (let index = 0; index < sample.length; index++) {
      if (sample[index] === 0) {
        if (index % 2 === 0) evenNulls++;
        else oddNulls++;
      }
    }
    if (oddNulls > sample.length / 8) encoding = "utf-16le";
    else if (evenNulls > sample.length / 8) encoding = "utf-16be";
  }
  return new TextDecoder(encoding).decode(data.subarray(offset)).replace(/^\uFEFF/, "");
}

function countDelimiter(line: string, delimiter: string): number {
  let quoted = false;
  let count = 0;
  for (let index = 0; index < line.length; index++) {
    if (line[index] === '"') {
      if (quoted && line[index + 1] === '"') index++;
      else quoted = !quoted;
    } else if (!quoted && line[index] === delimiter) {
      count++;
    }
  }
  return count;
}

function parseCsv(text: string): string[][] {
  const lines = text.split(/\r\n|\n|\r/);
  const directive = lines[0]?.trim().match(/^sep=(.)$/i);
  const sampleLines = lines.filter(line => line.trim() && !/^sep=./i.test(line.trim())).slice(0, 20);
  const delimiter = directive?.[1] ?? [",", ";", "\t"]
    .map(candidate => ({
      candidate,
      count: sampleLines.reduce((total, line) => total + countDelimiter(line, candidate), 0),
    }))
    .sort((left, right) => right.count - left.count)[0]?.candidate ?? ",";
  const csvText = directive ? lines.slice(1).join("\n") : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  for (let index = 0; index < csvText.length; index++) {
    const character = csvText[index];
    if (character === '"') {
      if (quoted && csvText[index + 1] === '"') {
        cell += '"';
        index++;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && character === delimiter) {
      row.push(cell);
      cell = "";
    } else if (!quoted && (character === "\n" || character === "\r")) {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      if (character === "\r" && csvText[index + 1] === "\n") index++;
    } else {
      cell += character;
    }
  }

  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

function isRosterHeader(row: string[]): boolean {
  const headers = row.map(normalizedHeader);
  const hasStudentName = headers.includes("studentname") || headers.includes("name")
    || (headers.includes("firstname") && headers.includes("lastname"));
  return hasStudentName && (
    headers.includes("admissionnumber") || headers.includes("admissionno")
    || headers.includes("admno") || headers.includes("class") || headers.includes("classname")
  );
}

function readExcelRows(bytes: ArrayBuffer): string[][] {
  const workbook = XLSX.read(bytes, { type: "array", raw: false });
  const sheets = workbook.SheetNames.map(name => XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[name], {
    header: 1,
    defval: "",
    raw: false,
    blankrows: true,
  }).map(row => row.map(value => String(value ?? ""))));
  if (!sheets.length) throw new Error("The Excel workbook does not contain a worksheet.");
  const hasStudentData = (rows: string[][]) => {
    const headerIndex = rows.findIndex(isRosterHeader);
    return headerIndex >= 0 && rows.slice(headerIndex + 1).some(row => row.some(value => value.trim() !== ""));
  };
  return sheets.find(hasStudentData)
    ?? sheets.find(rows => rows.some(isRosterHeader))
    ?? sheets.find(rows => rows.some(row => row.some(value => value.trim() !== "")))
    ?? [];
}

export async function parseSchoolRosterFile(file: File): Promise<SchoolStudentRosterImportRowInput[]> {
  const extension = file.name.toLowerCase().split(".").pop();
  if (!extension || !["csv", "xls", "xlsx"].includes(extension)) {
    throw new Error("Choose a CSV or Excel file (.csv, .xls, or .xlsx). Download the roster template to see the expected columns.");
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("This student file is larger than 5 MB. Split it into smaller files and import them separately.");
  }

  const bytes = await file.arrayBuffer();
  const sourceRows = extension === "csv" ? parseCsv(decodeCsv(bytes)) : readExcelRows(bytes);
  if (!sourceRows.length) throw new Error("The CSV file needs a header row.");
  const firstNonEmptyRow = sourceRows.findIndex(row => row.some(value => value.trim() !== ""));
  const headerRowIndex = sourceRows.findIndex(isRosterHeader);
  const detectedHeaderRowIndex = headerRowIndex >= 0 ? headerRowIndex : firstNonEmptyRow;
  if (detectedHeaderRowIndex < 0) throw new Error("The CSV file needs a header row.");
  const headers = sourceRows[detectedHeaderRowIndex].map(normalizedHeader);
  const columns = {
    admissionNumber: columnIndex(headers, ["admissionnumber", "admissionno", "admno"]),
    studentName: columnIndex(headers, ["studentname", "name"]),
    firstName: columnIndex(headers, ["firstname"]),
    lastName: columnIndex(headers, ["lastname"]),
    className: columnIndex(headers, ["class", "classname"]),
    guardianName: columnIndex(headers, ["guardianname", "parentname"]),
    guardianPhone: columnIndex(headers, ["guardianphone", "parentphone"]),
    guardianEmail: columnIndex(headers, ["guardianemail", "parentemail"]),
  };
  const cell = (row: unknown[], index: number) => index < 0 ? "" : String(row[index] ?? "").trim();
  const dataRows = sourceRows.slice(detectedHeaderRowIndex + 1)
    .map((row, index) => ({ row, sourceRow: detectedHeaderRowIndex + index + 2 }))
    .filter(({ row }) => row.some(value => String(value ?? "").trim() !== ""));
  if (!dataRows.length) throw new Error("No student rows found below the header. Make sure the CSV includes at least one populated row.");
  if (dataRows.length > MAX_DATA_ROWS) {
    throw new Error(`This CSV has more than ${MAX_DATA_ROWS.toLocaleString()} student rows. Split it into smaller files.`);
  }

  return dataRows.map(({ row, sourceRow }) => {
    const studentName = columns.studentName >= 0
      ? cell(row, columns.studentName)
      : [cell(row, columns.firstName), cell(row, columns.lastName)].filter(Boolean).join(" ");
    return {
      sourceRow,
      admissionNumber: cell(row, columns.admissionNumber),
      studentName,
      className: cell(row, columns.className),
      guardianName: cell(row, columns.guardianName),
      guardianPhone: cell(row, columns.guardianPhone),
      guardianEmail: cell(row, columns.guardianEmail),
    };
  });
}

export function downloadSchoolRosterTemplate(): void {
  const csv = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet([[
    "Admission Number",
    "Student Name",
    "Class",
    "Guardian Name",
    "Guardian Phone",
    "Guardian Email",
  ]]));
  downloadCsv(csv, "student-roster-template.csv");
}

export function downloadSchoolRosterResults(result: SchoolStudentRosterImportResponse): void {
  const escapeCell = (value: string | number) => {
    const safe = String(value).replace(/^[=+\-@]/, value => `'${value}`);
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const rows = [
    ["CSV row", "Admission Number", "Student Name", "Class", "Result", "Reason"],
    ...result.rows.map(row => [
      row.sourceRow,
      row.admissionNumber,
      row.studentName,
      row.className,
      row.outcome,
      row.reason ?? "",
    ]),
  ];
  downloadCsv(rows.map(row => row.map(escapeCell).join(",")).join("\r\n"), "student-roster-import-results.csv");
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}